/**
 * SchedulerSettingsCard - Zeitplan-Einstellungen für automatische Prozesse
 * Extrahiert aus SettingsPage.js für bessere Wartbarkeit
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Save, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SchedulerSettingsCard = () => {
  const [settings, setSettings] = useState({
    rent_invoice_day: 1,
    rent_invoice_hour: 8,
    rent_invoice_minute: 0,
    draft_invoice_day: 26,
    draft_invoice_hour: 8,
    draft_invoice_minute: 0,
    daily_billing_hour: 23,
    daily_billing_minute: 0,
    points_warning_hour: 18,
    points_warning_minute: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/scheduler`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching scheduler settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/scheduler`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scheduler-Einstellungen gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const restartScheduler = async () => {
    setRestarting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/scheduler/restart`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scheduler neu gestartet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Neustart');
    } finally {
      setRestarting(false);
    }
  };

  const formatTime = (hour, minute) => {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
          <p className="text-gray-500">Lade Scheduler-Einstellungen...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="scheduler-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock size={20} className="text-blue-600" />
          ⏰ Zeitplan-Einstellungen
        </CardTitle>
        <CardDescription>
          Konfigurieren Sie die Uhrzeiten für automatische Prozesse
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Miet-Rechnungen (automatisch) */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">🏠</span>
            <h4 className="font-semibold">Automatische Miet-Rechnungen</h4>
            <Badge variant="outline" className="ml-auto">
              Für Mieter mit fixer Miete
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="rent-day">Tag des Monats</Label>
              <Select 
                value={String(settings.rent_invoice_day)}
                onValueChange={(v) => setSettings({...settings, rent_invoice_day: parseInt(v)})}
              >
                <SelectTrigger id="rent-day" data-testid="rent-invoice-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                    <SelectItem key={day} value={String(day)}>{day}.</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rent-hour">Stunde</Label>
              <Select 
                value={String(settings.rent_invoice_hour)}
                onValueChange={(v) => setSettings({...settings, rent_invoice_hour: parseInt(v)})}
              >
                <SelectTrigger id="rent-hour" data-testid="rent-invoice-hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 24}, (_, i) => i).map(hour => (
                    <SelectItem key={hour} value={String(hour)}>{String(hour).padStart(2, '0')}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rent-minute">Minute</Label>
              <Select 
                value={String(settings.rent_invoice_minute)}
                onValueChange={(v) => setSettings({...settings, rent_invoice_minute: parseInt(v)})}
              >
                <SelectTrigger id="rent-minute" data-testid="rent-invoice-minute-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 15, 30, 45].map(minute => (
                    <SelectItem key={minute} value={String(minute)}>{String(minute).padStart(2, '0')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Aktuell: Am <strong>{settings.rent_invoice_day}.</strong> des Monats um <strong>{formatTime(settings.rent_invoice_hour, settings.rent_invoice_minute)}</strong> Uhr
          </p>
        </div>

        {/* Rechnungs-Entwürfe */}
        <div className="border rounded-lg p-4 border-orange-200 bg-orange-50/30">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">📝</span>
            <h4 className="font-semibold">Rechnungs-Entwürfe</h4>
            <Badge variant="outline" className="ml-auto bg-orange-100 text-orange-700">
              Für Mieter mit variabler Miete
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="draft-day">Tag des Monats</Label>
              <Select 
                value={String(settings.draft_invoice_day)}
                onValueChange={(v) => setSettings({...settings, draft_invoice_day: parseInt(v)})}
              >
                <SelectTrigger id="draft-day" data-testid="draft-invoice-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                    <SelectItem key={day} value={String(day)}>{day}.</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="draft-hour">Stunde</Label>
              <Select 
                value={String(settings.draft_invoice_hour)}
                onValueChange={(v) => setSettings({...settings, draft_invoice_hour: parseInt(v)})}
              >
                <SelectTrigger id="draft-hour" data-testid="draft-invoice-hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 24}, (_, i) => i).map(hour => (
                    <SelectItem key={hour} value={String(hour)}>{String(hour).padStart(2, '0')}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="draft-minute">Minute</Label>
              <Select 
                value={String(settings.draft_invoice_minute)}
                onValueChange={(v) => setSettings({...settings, draft_invoice_minute: parseInt(v)})}
              >
                <SelectTrigger id="draft-minute" data-testid="draft-invoice-minute-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 15, 30, 45].map(minute => (
                    <SelectItem key={minute} value={String(minute)}>{String(minute).padStart(2, '0')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Aktuell: Am <strong>{settings.draft_invoice_day}.</strong> des Monats um <strong>{formatTime(settings.draft_invoice_hour, settings.draft_invoice_minute)}</strong> Uhr
          </p>
          <p className="text-xs text-orange-600 mt-1">
            💡 Tipp: Setzen Sie diesen Tag vor den automatischen Rechnungen, um genug Zeit zur Überprüfung zu haben.
          </p>
        </div>

        {/* Tägliche Abrechnung */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">💳</span>
            <h4 className="font-semibold">Tägliche Termin-Abrechnung</h4>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="billing-hour">Stunde</Label>
              <Select 
                value={String(settings.daily_billing_hour)}
                onValueChange={(v) => setSettings({...settings, daily_billing_hour: parseInt(v)})}
              >
                <SelectTrigger id="billing-hour" data-testid="daily-billing-hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 24}, (_, i) => i).map(hour => (
                    <SelectItem key={hour} value={String(hour)}>{String(hour).padStart(2, '0')}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="billing-minute">Minute</Label>
              <Select 
                value={String(settings.daily_billing_minute)}
                onValueChange={(v) => setSettings({...settings, daily_billing_minute: parseInt(v)})}
              >
                <SelectTrigger id="billing-minute" data-testid="daily-billing-minute-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 15, 30, 45].map(minute => (
                    <SelectItem key={minute} value={String(minute)}>{String(minute).padStart(2, '0')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Aktuell: Täglich um <strong>{formatTime(settings.daily_billing_hour, settings.daily_billing_minute)}</strong> Uhr
          </p>
        </div>

        {/* Aktions-Buttons */}
        <div className="flex gap-3 pt-4 border-t">
          <Button onClick={saveSettings} disabled={saving} data-testid="save-scheduler-settings-btn">
            <Save size={16} className="mr-2" />
            {saving ? 'Speichern...' : 'Einstellungen speichern'}
          </Button>
          <Button variant="outline" onClick={restartScheduler} disabled={restarting}>
            <RefreshCw size={16} className={`mr-2 ${restarting ? 'animate-spin' : ''}`} />
            {restarting ? 'Neustart...' : 'Scheduler neu starten'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SchedulerSettingsCard;
